import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarsArrowDownIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageOrderModal } from 'components/Modals/PageOrder'
import { FieldOrigin, getInitialLoanFields } from 'config'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { ReportPlaceholders } from 'pages/Loan/Reports/ReportPlaceholders'
import { IReportHeaders } from 'pages/Loan/Reports/types'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getLoanReportsPlaceholders } from 'services'
import { Button, ButtonGroup, Input2, Modal, Tooltip } from 'stories/components'
import {
  getUnusedLoanDetailsFields,
  LoanDetailsFieldConfig,
  LoanDetailsSection,
  normalizeLoanDetailsFieldConfig,
  toLoanFieldConfigKey,
  toReportFieldKey,
} from 'stories/layouts/FloaterTabs/loanDetails.constants'
import { confirm, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

type Props = {
  value?: Partial<LoanDetailsFieldConfig> | null
  onChange: (config: LoanDetailsFieldConfig) => void
  disabled?: boolean
}

type AddMode = 'builtin' | 'report' | 'loanField'

type LoanStructureOption = {
  key: string
  title: string
  sectionTitle: string
  isCustom: boolean
}

export const LoanDetailsFieldsConfig = ({ value, onChange, disabled }: Props) => {
  const config = useMemo(() => normalizeLoanDetailsFieldConfig(value), [value])
  const loanFieldProperties = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.LoanFields] || {},
  )

  const [orderSectionId, setOrderSectionId] = useState<string | null>(null)
  const [addToSectionId, setAddToSectionId] = useState<string | null>(null)
  const [addMode, setAddMode] = useState<AddMode>('loanField')
  const [selectedAddField, setSelectedAddField] = useState('')
  const [selectedReportPlaces, setSelectedReportPlaces] = useState<string[]>([])
  const [selectedLoanFields, setSelectedLoanFields] = useState<string[]>([])
  const [loanFieldSearch, setLoanFieldSearch] = useState('')
  const [placeholders, setPlaceholders] = useState<Record<string, IReportHeaders[]>>({ Loan: [] })
  const [placeholderType, setPlaceholderType] = useState('Loan')
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false)

  const unusedFields = getUnusedLoanDetailsFields(config)
  const usedReportKeys = useMemo(() => {
    const used = new Set(config.sections.flatMap((s) => s.fields))
    const keys = new Set<string>()
    used.forEach((fieldKey) => {
      const def = config.fields[fieldKey]
      if (def?.source === 'report' && def.reportKey) keys.add(def.reportKey)
      else if (fieldKey.startsWith('rpt:')) keys.add(fieldKey.slice(4))
    })
    return keys
  }, [config])

  const usedLoanFieldKeys = useMemo(() => {
    const used = new Set(config.sections.flatMap((s) => s.fields))
    const keys = new Set<string>()
    used.forEach((configKey) => {
      const def = config.fields[configKey]
      if (def?.source === 'loanField' && def.fieldKey) keys.add(def.fieldKey)
      else if (configKey.startsWith('lf:')) keys.add(configKey.slice(3))
    })
    return keys
  }, [config])

  const loanStructureOptions = useMemo<LoanStructureOption[]>(() => {
    const defaults = getInitialLoanFields()
    const structureProps = loanFieldProperties[FieldOrigin.LoanStructure] || {}
    const sections = loanFieldProperties.sections?.[FieldOrigin.LoanStructure] || []
    const options: LoanStructureOption[] = []
    const placed = new Set<string>()

    sections.forEach((section: { id?: number; title?: string; fields?: string[] }, idx: number) => {
      const sectionTitle = section.title || `Section ${section.id ?? idx + 1}`
      ;(section.fields || []).forEach((fieldKey: string) => {
        if (placed.has(fieldKey)) return
        placed.add(fieldKey)
        const title =
          structureProps[fieldKey]?.title ||
          (defaults[fieldKey] as any)?.title ||
          fieldKey
        options.push({
          key: fieldKey,
          title,
          sectionTitle,
          isCustom: isCustomFieldKey(fieldKey),
        })
      })
    })

    // Include any default / custom fields not listed in sections
    const remainingKeys = new Set([
      ...Object.keys(defaults),
      ...Object.keys(structureProps).filter((k) => typeof structureProps[k] === 'object' && structureProps[k] != null),
    ])
    remainingKeys.forEach((fieldKey) => {
      if (placed.has(fieldKey)) return
      // Skip non-field metadata keys if any slipped in
      if (['section', 'order', 'visibility'].includes(fieldKey)) return
      const prop = structureProps[fieldKey]
      const defaultInput = defaults[fieldKey] as any
      if (!prop && !defaultInput) return
      options.push({
        key: fieldKey,
        title: prop?.title || defaultInput?.title || fieldKey,
        sectionTitle: 'Other',
        isCustom: isCustomFieldKey(fieldKey),
      })
    })

    return options
  }, [loanFieldProperties])

  const filteredLoanStructureOptions = useMemo(() => {
    const q = loanFieldSearch.trim().toLowerCase()
    if (!q) return loanStructureOptions
    return loanStructureOptions.filter(
      (opt) => opt.title.toLowerCase().includes(q) || opt.key.toLowerCase().includes(q),
    )
  }, [loanStructureOptions, loanFieldSearch])

  const loanOptionsBySection = useMemo(() => {
    const map: Record<string, LoanStructureOption[]> = {}
    filteredLoanStructureOptions.forEach((opt) => {
      if (!map[opt.sectionTitle]) map[opt.sectionTitle] = []
      map[opt.sectionTitle].push(opt)
    })
    return map
  }, [filteredLoanStructureOptions])

  useEffect(() => {
    if (!addToSectionId || addMode !== 'report') return
    if (placeholders.Loan?.length) return
    setLoadingPlaceholders(true)
    getLoanReportsPlaceholders()
      .then((res) => setPlaceholders(res || { Loan: [] }))
      .finally(() => setLoadingPlaceholders(false))
  }, [addToSectionId, addMode, placeholders.Loan?.length])

  const commit = (next: LoanDetailsFieldConfig) => onChange(next)

  const toggleVisible = (key: string) => {
    const next = cloneDeep(config)
    next.fields[key].visible = next.fields[key].visible === false
    commit(next)
  }

  const renameField = async (key: string) => {
    const title: any = await prompt('Field title', { value: config.fields[key]?.title || key })
    if (title === false || title == null || String(title).trim() === '') return
    const next = cloneDeep(config)
    next.fields[key].title = String(title).trim()
    commit(next)
  }

  const removeField = async (key: string, sectionId: string) => {
    const ok = await confirm('Remove this field from the Loan Details floater? You can add it back later.')
    if (!ok) return
    const next = cloneDeep(config)
    const section = next.sections.find((s) => s.id === sectionId)
    if (section) section.fields = section.fields.filter((k) => k !== key)
    commit(next)
  }

  const moveFieldToSection = (key: string, fromSectionId: string, toSectionId: string) => {
    if (fromSectionId === toSectionId) return
    const next = cloneDeep(config)
    const from = next.sections.find((s) => s.id === fromSectionId)
    const to = next.sections.find((s) => s.id === toSectionId)
    if (!from || !to) return
    from.fields = from.fields.filter((k) => k !== key)
    if (!to.fields.includes(key)) to.fields.push(key)
    commit(next)
  }

  const addSection = async () => {
    const title: any = await prompt('Section title')
    if (!title || String(title).trim() === '') return
    const next = cloneDeep(config)
    next.sections.push({
      id: `section_${Date.now()}`,
      title: String(title).trim(),
      fields: [],
    })
    commit(next)
  }

  const editSectionTitle = async (idx: number) => {
    const title: any = await prompt('Section title', { value: config.sections[idx].title })
    if (title === false || title == null || String(title).trim() === '') return
    const next = cloneDeep(config)
    next.sections[idx].title = String(title).trim()
    commit(next)
  }

  const moveSection = (idx: number, direction: number) => {
    const next = cloneDeep(config)
    next.sections.splice(idx + direction, 0, next.sections.splice(idx, 1)[0])
    commit(next)
  }

  const removeSection = async (idx: number) => {
    const ok = await confirm('Remove this section? Fields in it will become available to add again.')
    if (!ok) return
    const next = cloneDeep(config)
    next.sections.splice(idx, 1)
    commit(next)
  }

  const onCloseSectionOrderModal = (newInputs: { title: string; fieldKey?: string }[] | null) => {
    if (newInputs && orderSectionId) {
      const next = cloneDeep(config)
      const section = next.sections.find((s) => s.id === orderSectionId)
      if (section) section.fields = newInputs.map((item: any) => item.fieldKey).filter(Boolean)
      commit(next)
    }
    setOrderSectionId(null)
  }

  const closeAddModal = () => {
    setAddToSectionId(null)
    setSelectedAddField('')
    setSelectedReportPlaces([])
    setSelectedLoanFields([])
    setLoanFieldSearch('')
    setAddMode('loanField')
  }

  const toggleLoanFieldSelection = (fieldKey: string) => {
    if (usedLoanFieldKeys.has(fieldKey)) return
    setSelectedLoanFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((k) => k !== fieldKey) : [...prev, fieldKey],
    )
  }

  const addFieldToSection = () => {
    if (!addToSectionId) return
    const next = cloneDeep(config)
    const section = next.sections.find((s) => s.id === addToSectionId)
    if (!section) return

    if (addMode === 'builtin') {
      if (!selectedAddField) return
      if (!section.fields.includes(selectedAddField)) section.fields.push(selectedAddField)
    } else if (addMode === 'loanField') {
      const toAdd = selectedLoanFields.filter((k) => !usedLoanFieldKeys.has(k))
      if (!toAdd.length) return
      toAdd.forEach((loanFieldKey) => {
        const opt = loanStructureOptions.find((o) => o.key === loanFieldKey)
        const configKey = toLoanFieldConfigKey(loanFieldKey)
        next.fields[configKey] = {
          title: opt?.title || loanFieldKey,
          visible: true,
          source: 'loanField',
          fieldKey: loanFieldKey,
        }
        if (!section.fields.includes(configKey)) section.fields.push(configKey)
      })
    } else {
      const toAdd = selectedReportPlaces.filter((p) => !usedReportKeys.has(p))
      if (!toAdd.length) return
      toAdd.forEach((placeholder) => {
        const fieldKey = toReportFieldKey(placeholder)
        next.fields[fieldKey] = {
          title: placeholder,
          visible: true,
          source: 'report',
          reportKey: placeholder,
        }
        if (!section.fields.includes(fieldKey)) section.fields.push(fieldKey)
      })
    }

    commit(next)
    closeAddModal()
  }

  const canAdd =
    addMode === 'builtin'
      ? !!selectedAddField && unusedFields.length > 0
      : addMode === 'loanField'
        ? selectedLoanFields.some((k) => !usedLoanFieldKeys.has(k))
        : selectedReportPlaces.some((p) => !usedReportKeys.has(p))

  const renderField = (key: string, section: LoanDetailsSection) => {
    const field = config.fields[key]
    if (!field) return null
    const isVisible = field.visible !== false
    const VisibleIcon = isVisible ? EyeIcon : EyeSlashIcon
    const sourceLabel =
      field.source === 'report' ? 'report' : field.source === 'loanField' ? 'loan' : null

    return (
      <div
        key={key}
        className={`flex items-center gap-2 border rounded px-2 py-1.5 mb-1 bg-white ${
          isVisible ? '' : 'opacity-40'
        }`}
      >
        <span className="flex-1 text-sm truncate" title={field.title}>
          {field.title}
          {sourceLabel && (
            <span
              className={`ml-1 text-[10px] uppercase ${
                sourceLabel === 'report' ? 'text-indigo-500' : 'text-emerald-600'
              }`}
            >
              {sourceLabel}
            </span>
          )}
        </span>
        <select
          className="text-xs border-gray-200 py-0.5 max-w-[140px]"
          disabled={disabled}
          value={section.id}
          title="Move to section"
          onChange={(e) => moveFieldToSection(key, section.id, e.target.value)}
        >
          {config.sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
        <Tooltip message={isVisible ? 'Hide' : 'Show'}>
          <span
            className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
            onClick={() => !disabled && toggleVisible(key)}
          >
            <VisibleIcon className="w-4 h-4 text-gray-500" />
          </span>
        </Tooltip>
        <Tooltip message="Rename">
          <span
            className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
            onClick={() => !disabled && renameField(key)}
          >
            <PencilSquareIcon className="w-4 h-4 text-gray-500" />
          </span>
        </Tooltip>
        <Tooltip message="Remove">
          <span
            className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
            onClick={() => !disabled && removeField(key, section.id)}
          >
            <TrashIcon className="w-4 h-4 text-red-500" />
          </span>
        </Tooltip>
      </div>
    )
  }

  const renderSection = (section: LoanDetailsSection, sectionIdx: number) => (
    <div key={section.id} className="mb-4 border rounded-md p-3 bg-slate-50">
      <RenderInput
        Key={`section-${section.id}`}
        input={{
          inputType: 'section',
          title: section.title,
          additionalElements: (
            <div className="flex gap-1">
              <Tooltip message="Add field to this section">
                <span
                  className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
                  onClick={() => {
                    if (disabled) return
                    setAddToSectionId(section.id)
                    setSelectedAddField(unusedFields[0] || '')
                    setSelectedReportPlaces([])
                    setSelectedLoanFields([])
                    setAddMode('loanField')
                  }}
                >
                  <PlusIcon className="w-4 h-4" />
                </span>
              </Tooltip>
              <Tooltip message="Update field order">
                <span
                  className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
                  onClick={() => !disabled && setOrderSectionId(section.id)}
                >
                  <BarsArrowDownIcon className="w-4 h-4" />
                </span>
              </Tooltip>
              {sectionIdx > 0 && (
                <span
                  className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
                  onClick={() => !disabled && moveSection(sectionIdx, -1)}
                >
                  <ArrowUpIcon className="w-4 h-4" />
                </span>
              )}
              {sectionIdx < config.sections.length - 1 && (
                <span
                  className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
                  onClick={() => !disabled && moveSection(sectionIdx, 1)}
                >
                  <ArrowDownIcon className="w-4 h-4" />
                </span>
              )}
              <span
                className={`btn-icon ${disabled ? 'pointer-events-none' : ''}`}
                onClick={() => !disabled && editSectionTitle(sectionIdx)}
              >
                <PencilSquareIcon className="w-4 h-4" />
              </span>
              <span
                className={`btn-icon !text-red-500 ${disabled ? 'pointer-events-none' : ''}`}
                onClick={() => !disabled && removeSection(sectionIdx)}
              >
                <TrashIcon className="w-4 h-4" />
              </span>
            </div>
          ),
        }}
        onChange={() => {}}
      />
      <div className="mt-2">{section.fields.map((key) => renderField(key, section))}</div>
      {section.fields.length === 0 && <p className="text-xs text-gray-400 italic mt-1">No fields in this section</p>}
    </div>
  )

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold text-gray-800">Loan Details Fields</p>
        <Button link disabled={disabled} onClick={addSection}>
          <span className="flex items-center gap-1">
            <PlusIcon className="w-4 h-4" />
            Add Section
          </span>
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Show/hide fields, reorder within a section, move fields between sections, or remove them. You can also add any
        Loan Structure field or Report placeholder. Changes save with the Floater Tabs config.
      </p>

      {config.sections.map(renderSection)}

      {unusedFields.length > 0 && (
        <div className="mt-2 text-xs text-gray-500">
          Built-in available to add ({unusedFields.length}):{' '}
          {unusedFields.map((k) => config.fields[k]?.title || k).join(', ')}
        </div>
      )}

      {orderSectionId &&
        (() => {
          const section = config.sections.find((s) => s.id === orderSectionId)
          const orderInputs = section
            ? section.fields
                .filter((k) => config.fields[k])
                .map((k) => ({ fieldKey: k, title: config.fields[k].title || k }))
            : []
          return <PageOrderModal key="loanDetailsOrder" inputs={orderInputs} onClose={onCloseSectionOrderModal} />
        })()}

      {addToSectionId && (
        <Modal
          isOpen
          title="Add Field"
          onClose={closeAddModal}
          onOk={addFieldToSection}
          titleOkay="Add"
          disabled={!canAdd}
        >
          <div className="w-[52rem] max-h-[70vh] overflow-auto relative">
            <ButtonGroup
              title={{
                loanField: 'Loan Fields',
                report: 'Report Fields',
                builtin: 'Built-in Fields',
              }}
              value={addMode}
              onChange={(v: string) => setAddMode(v as AddMode)}
            />
            {addMode === 'builtin' ? (
              <div className="mt-3">
                {unusedFields.length === 0 ? (
                  <p className="text-sm text-gray-500">All built-in fields are already assigned to a section.</p>
                ) : (
                  <RenderInput
                    Key="addField"
                    input={{
                      inputType: 'select',
                      title: 'Field',
                      options: unusedFields.reduce((acc, k) => {
                        acc[k] = config.fields[k]?.title || k
                        return acc
                      }, {} as Record<string, string>),
                      value: selectedAddField,
                      hasDefaultOption: true,
                    }}
                    onChange={(_: string, v: string) => setSelectedAddField(v)}
                  />
                )}
              </div>
            ) : addMode === 'loanField' ? (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2">
                  Select any default or custom fields from Loan Field Properties → Loan Structure. Already-assigned
                  fields stay selected and will be skipped on add.
                </p>
                <div className="mb-3">
                  <Input2
                    type="search"
                    title="Search loan fields"
                    value={loanFieldSearch}
                    onChange={(v: string) => setLoanFieldSearch(v)}
                  />
                </div>
                {loanStructureOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No Loan Structure fields found.</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(loanOptionsBySection).map(([sectionTitle, opts]) => (
                      <div key={sectionTitle}>
                        <p className="text-xs font-semibold text-gray-600 border-b pb-1 mb-1">{sectionTitle}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {opts.map((opt) => {
                            const alreadyUsed = usedLoanFieldKeys.has(opt.key)
                            const isSelected = alreadyUsed || selectedLoanFields.includes(opt.key)
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                disabled={alreadyUsed}
                                onClick={() => toggleLoanFieldSelection(opt.key)}
                                className={`flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded border ${
                                  isSelected
                                    ? 'border-shade-blue bg-blue-50'
                                    : 'border-transparent hover:bg-gray-50'
                                } ${alreadyUsed ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
                                title={opt.key}
                              >
                                <span
                                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                    isSelected ? 'bg-shade-blue border-shade-blue' : 'border-gray-300'
                                  }`}
                                >
                                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                                </span>
                                <span className="truncate flex-1">{opt.title}</span>
                                {opt.isCustom && (
                                  <span className="text-[10px] uppercase text-amber-600 shrink-0">custom</span>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-3 relative">
                <LayoutLoading show={loadingPlaceholders} />
                <p className="text-xs text-gray-500 mb-2">
                  Select any fields available in Reports. Already-assigned placeholders stay selected and will be
                  skipped on add.
                </p>
                {!!placeholders.Loan?.length && (
                  <ReportPlaceholders
                    placeholders={placeholders}
                    selectedPlaces={[...new Set([...selectedReportPlaces, ...usedReportKeys])]}
                    setSelectedPlaces={(next: string[] | ((prev: string[]) => string[])) => {
                      const resolved =
                        typeof next === 'function' ? next([...selectedReportPlaces, ...usedReportKeys]) : next
                      setSelectedReportPlaces(resolved.filter((p) => !usedReportKeys.has(p)))
                    }}
                    type={placeholderType}
                    setType={setPlaceholderType}
                  />
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
